import type {ReactNode} from 'react'
import {
  AlertFillIcon,
  CheckCircleFillIcon,
  CommentIcon,
  CopilotIcon,
  DotFillIcon,
  FileDiffIcon,
  GitPullRequestDraftIcon,
  XCircleFillIcon,
} from '@primer/octicons-react'
import {isFailingOrIncompleteStatus} from '@github-ui/mergebox/page-data/payloads/status-checks'
import type {ConflictsSectionStatuses} from '@github-ui/mergebox/sections/ConflictsSection'
import {useMergeboxStatusCalculator} from '@github-ui/mergebox/use-mergebox-status-calculator'
import type {ReviewRequestsData} from '../../../page-data/payloads/review-requests'
import {ConsolidatedReviewState} from '@github-ui/mergebox/sections/ReviewerSection'
import {ActionItem} from './ActionItem'
import {CollapsibleActionItems} from './CollapsibleActionItems'
import {CompletedItemsDisclosure, type CompletedItem} from './CompletedItems'
import type {ValidatedMergeStatusButtonData} from '../../../hooks/use-merge-status-button-data'
import {isCopilot} from '@github-ui/assignees/copilot-user'

interface AuthorActionsProps {
  reviewers: ReviewRequestsData
  mergeStatusButtonData: ValidatedMergeStatusButtonData | undefined
}

interface AuthorActionsComponentProps {
  reviewers: ReviewRequestsData
  statusChecksData: ReturnType<typeof useMergeboxStatusCalculator>['statusChecksData']
  calculator: ReturnType<typeof useMergeboxStatusCalculator>['calculator']
  pullRequest: ReturnType<typeof useMergeboxStatusCalculator>['pullRequest']
}

/**
 * Container component for author action items.
 * This component is responsible for fetching the necessary data and calculating which action items to show for the author of the pull request.
 * The actual rendering of the action items is delegated to the AuthorActionsComponent.
 */
export function AuthorActions({reviewers, mergeStatusButtonData}: AuthorActionsProps) {
  const {calculator, pullRequest, statusChecksData} = useMergeboxStatusCalculator({
    headSha: mergeStatusButtonData?.headSha ?? '',
    channels: mergeStatusButtonData?.channels ?? null,
  })

  return (
    <AuthorActionsComponent
      reviewers={reviewers}
      statusChecksData={statusChecksData}
      calculator={calculator}
      pullRequest={pullRequest}
    />
  )
}

/**
 * Presentational component for author action items.
 * We separate this from the main AuthorActions component to isolate the logic for calculating which action items to
 * show from the data fetching and state management logic in the main AuthorActions component.
 */
export function AuthorActionsComponent({
  reviewers,
  statusChecksData,
  calculator,
  pullRequest,
}: AuthorActionsComponentProps) {
  const statusChecks = statusChecksData.statusChecks
  const conflictsSection = calculator.sections.ConflictsSection
  const reviewerSection = calculator.sections.ReviewerSection

  const failingChecks = statusChecks.filter(c => isFailingOrIncompleteStatus(c.state))
  const failingRequiredChecks = failingChecks.filter(c => c.isRequired)

  const reviewsWithUnresolved = reviewers.completedReviews
    .map(review => ({
      login: review.reviewer.displayLogin,
      unresolved: review.threadCounts.total - review.threadCounts.resolved,
    }))
    .filter(r => r.unresolved > 0)

  const copilotUnresolved = reviewsWithUnresolved.filter(r => isCopilot(r.login))
  const humanUnresolved = reviewsWithUnresolved.filter(r => !isCopilot(r.login))

  const copilotUnresolvedCount = copilotUnresolved.reduce((sum, r) => sum + r.unresolved, 0)

  const items: ReactNode[] = []
  const completedItems: CompletedItem[] = []

  if (pullRequest.isDraft) {
    const hasOutstandingIssues =
      failingRequiredChecks.length > 0 ||
      hasConflicts(conflictsSection) ||
      reviewerSection.sectionStatus === 'CHANGES_REQUESTED'

    const description = hasOutstandingIssues
      ? 'Address outstanding issues before marking as ready for review.'
      : 'No outstanding issues. This pull request may be ready for review.'

    items.push(
      <ActionItem
        key="draft"
        leadingIcon={GitPullRequestDraftIcon}
        title="Mark as ready for review"
        description={description}
        action={{
          panel: 'mergeStatus',
          label: 'Ready for review',
          variant: 'default',
          analyticsPayload: {section: 'author_actions', context: 'draft'},
        }}
      />,
    )
  }

  if (hasConflicts(conflictsSection)) {
    const conflictDescription = getConflictDescription(conflictsSection.sectionStatus)
    items.push(
      <ActionItem
        key="merge-conflicts"
        leadingIcon={AlertFillIcon}
        title="Merge conflicts must be resolved"
        variant="warning"
        description={conflictDescription}
        action={{
          panel: 'mergeStatus',
          label: 'Resolve',
          analyticsPayload: {section: 'author_actions', context: 'merge_conflicts'},
        }}
      />,
    )
  }
  if (conflictsSection.shouldRender && conflictsSection.mergeBoxStatus === 'PASSED') {
    completedItems.push({label: 'No merge conflicts'})
  }

  if (reviewerSection.shouldRender && reviewerSection.sectionStatus === 'CHANGES_REQUESTED') {
    const changesRequestedReviews = reviewers.completedReviews.filter(r => r.state === 'changes_requested')

    items.push(
      <ActionItem
        key="changes-requested"
        leadingIcon={FileDiffIcon}
        variant="danger"
        title="Changes have been requested"
        action={{
          panel: 'comments',
          label: 'View',
          count: changesRequestedReviews.length,
          metadata: {hideResolved: true},
          analyticsPayload: {
            section: 'author_actions',
            context: 'changes_requested',
          },
        }}
      />,
    )
  } else if (reviewerSection.shouldRender && reviewerSection.sectionStatus === 'REVIEW_REQUIRED') {
    const reviewTitle =
      reviewerSection.numReviewsRequired > 0
        ? `Waiting for ${reviewerSection.numReviewsRequired} required review${reviewerSection.numReviewsRequired === 1 ? '' : 's'}`
        : 'Waiting for review'
    items.push(
      <ActionItem
        key="waiting-for-review"
        leadingIcon={DotFillIcon}
        variant="warning"
        title={reviewTitle}
        description="No action needed — waiting for reviewers to submit their feedback."
      />,
    )
  }

  if (reviewerSection.shouldRender) {
    if (reviewerSection.sectionStatus === ConsolidatedReviewState.APPROVED) {
      completedItems.push({label: 'Required reviewers approved'})
    }
    if (reviewerSection.sectionStatus === ConsolidatedReviewState.REVIEWED) {
      const hasApproval = reviewers.completedReviews.some(r => r.state === 'approved')
      completedItems.push({label: hasApproval ? 'Reviewers approved' : 'Reviewers reviewed'})
    }
  }

  if (failingRequiredChecks.length > 0) {
    items.push(
      <ActionItem
        key="failing-required-checks"
        leadingIcon={XCircleFillIcon}
        variant="danger"
        title="Some checks are failing"
        action={{
          panel: 'mergeStatus',
          label: 'View',
          count: failingRequiredChecks.length,
          analyticsPayload: {section: 'author_actions', context: 'failing_required_checks'},
        }}
      />,
    )
  }

  if (copilotUnresolvedCount > 0) {
    items.push(
      <ActionItem
        key="copilot-unresolved-comments"
        leadingIcon={CopilotIcon}
        title="Unresolved Copilot comments"
        description="Review and resolve comments from Copilot code review."
        action={{
          panel: 'comments',
          label: 'View',
          count: copilotUnresolvedCount,
          metadata: {selectedAuthor: 'Copilot', hideResolved: true},
          analyticsPayload: {section: 'author_actions', context: 'copilot_unresolved_comments'},
        }}
      />,
    )
  }

  if (humanUnresolved.length > 0) {
    const humanUnresolvedCount = humanUnresolved.reduce((sum, r) => sum + r.unresolved, 0)

    items.push(
      <ActionItem
        key="unresolved-comments"
        leadingIcon={CommentIcon}
        title="Unresolved comments"
        action={{
          panel: 'comments',
          label: 'View',
          count: humanUnresolvedCount,
          metadata: {hideResolved: true},
          analyticsPayload: {
            section: 'author_actions',
            context: 'human_unresolved_comments',
          },
        }}
      />,
    )
  }

  // const hasSuggestedReviewers = reviewers.suggestedReviewers.length > 0

  // if (hasSuggestedReviewers) {
  //   items.push(<SuggestedReviewers key="add-reviewers" suggestedReviewers={reviewers.suggestedReviewers} />)
  // }

  if (reviewers.completedReviews.length > 0) {
    const totalResolved = reviewers.completedReviews.reduce((sum, rev) => sum + rev.threadCounts.resolved, 0)
    if (totalResolved > 0) {
      completedItems.push({
        label: `${totalResolved} comment${totalResolved === 1 ? '' : 's'} resolved`,
      })
    }
  }

  if (items.length === 0) {
    items.push(
      <ActionItem
        key="no-action-items"
        leadingIcon={CheckCircleFillIcon}
        variant="success"
        title="This PR may be ready to merge"
        description="Review the pull request details and merge when ready."
        action={{
          panel: 'mergeStatus',
          label: 'Ready to merge',
          variant: 'primary',
          analyticsPayload: {section: 'author_actions', context: 'no_action_items'},
        }}
      />,
    )
  }

  return (
    <>
      <CollapsibleActionItems items={items} />
      <CompletedItemsDisclosure items={completedItems} />
    </>
  )
}

type ConflictsSectionData = ReturnType<typeof useMergeboxStatusCalculator>['calculator']['sections']['ConflictsSection']

function hasConflicts(section: ConflictsSectionData): boolean {
  return (
    section.shouldRender &&
    (section.sectionStatus === 'HAS_CONFLICTS' || section.sectionStatus === 'HAS_REBASE_CONFLICTS')
  )
}

function getConflictDescription(status: ConflictsSectionStatuses): string {
  if (status === 'HAS_REBASE_CONFLICTS') {
    return 'This branch has rebase conflicts that must be resolved locally.'
  }
  return 'This branch has conflicts that must be resolved.'
}
